import { Capacitor } from '@capacitor/core';
import { api } from './api';

let listenersReady = false;
let lastToken: string | null = null;

export function isNativePushPlatform(): boolean {
  return Capacitor.isNativePlatform();
}

async function ensureListeners() {
  if (listenersReady) return;
  listenersReady = true;

  const mod = await import('@capacitor/push-notifications');
  const PushNotifications = mod.PushNotifications;

  PushNotifications.addListener('registration', async (token) => {
    try {
      const value = (token as any)?.value ? String((token as any).value) : '';
      if (!value) return;
      lastToken = value;
      await api.post('/api/push/mobile/subscribe', {
        token: value,
        platform: Capacitor.getPlatform(),
        device: (navigator.userAgent || '').slice(0, 200),
      });
    } catch {
      // best-effort
    }
  });

  PushNotifications.addListener('registrationError', () => {
    // ignore (best-effort)
  });

  PushNotifications.addListener('pushNotificationReceived', async (notification) => {
    try {
      // When app is in foreground, Android may not show a system notification.
      // We show a local notification so the user always "sees" the push.
      const title = String((notification as any)?.title || 'Notificare');
      const body = String((notification as any)?.body || '');
      const data = (notification as any)?.data || {};

      const lnMod = await import('@capacitor/local-notifications');
      const LocalNotifications = lnMod.LocalNotifications;
      const perm = await LocalNotifications.checkPermissions();
      if ((perm as any)?.display !== 'granted') {
        await LocalNotifications.requestPermissions();
      }
      await LocalNotifications.schedule({
        notifications: [
          {
            id: Date.now() % 2147483647,
            title: title.slice(0, 200),
            body: body.slice(0, 500),
            extra: { ...data },
          },
        ],
      });
    } catch {
      // best-effort
    }
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    try {
      const data = (action as any)?.notification?.data || {};
      const url = String(data?.url || data?.link || '/notifications');
      if (url) window.location.href = url.startsWith('/') ? url : `/${url}`;
    } catch {
      // ignore
    }
  });
}

export async function getNativePushEnabled(): Promise<boolean> {
  if (!isNativePushPlatform()) return false;
  try {
    const resp = await api.get('/api/push/mobile/status');
    return Boolean((resp.data as any)?.enabled);
  } catch {
    return false;
  }
}

export async function enableNativePush(): Promise<{ ok: boolean; detail?: string }> {
  if (!isNativePushPlatform()) return { ok: false, detail: 'Nu esti pe mobil.' };
  await ensureListeners();
  const mod = await import('@capacitor/push-notifications');
  const PushNotifications = mod.PushNotifications;

  const perm = await PushNotifications.requestPermissions();
  if ((perm as any)?.receive !== 'granted') return { ok: false, detail: 'Permisiune refuzata.' };

  // Also request local notifications permission (for foreground display).
  try {
    const lnMod = await import('@capacitor/local-notifications');
    const LocalNotifications = lnMod.LocalNotifications;
    const lperm = await LocalNotifications.checkPermissions();
    if ((lperm as any)?.display !== 'granted') await LocalNotifications.requestPermissions();
  } catch {
    // ignore
  }

  // Trigger (re)registration. Token will be saved via the 'registration' listener.
  await PushNotifications.register();
  return { ok: true };
}

export async function disableNativePush(): Promise<{ ok: boolean; detail?: string }> {
  if (!isNativePushPlatform()) return { ok: true };
  await ensureListeners();
  const mod = await import('@capacitor/push-notifications');
  const PushNotifications = mod.PushNotifications;

  try {
    if (lastToken) await api.post('/api/push/mobile/unsubscribe', { token: lastToken });
    else await api.post('/api/push/mobile/unsubscribe-all', {});
  } catch {
    // best-effort
  }

  try {
    await PushNotifications.unregister();
  } catch {
    // ignore
  }

  lastToken = null;
  return { ok: true };
}

export async function ensureNativePushRegisteredIfGranted(): Promise<void> {
  if (!isNativePushPlatform()) return;
  await ensureListeners();
  try {
    const mod = await import('@capacitor/push-notifications');
    const PushNotifications = mod.PushNotifications;
    const perm = await PushNotifications.checkPermissions();
    if ((perm as any)?.receive === 'granted') {
      await PushNotifications.register();
    }
  } catch {
    // ignore
  }
}
